import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import {
  cancelAppointment,
  listMyAppointmentsPatient,
  normalizeAppointmentList,
  PATIENT_APPTS_QUERY_KEY,
} from "@/api/appointmentApi";
import { browserTimeZone } from "@/lib/clinicTime";
import { formatSlotStart } from "@/lib/appointmentTimeFormat";
import { PageHeader } from "@/components/common/PageHeader";
import { AppointmentStatusBadge } from "@/components/appointment/AppointmentStatusBadge";
import {
  appointmentBucket,
  canCancelAppointment,
  canJoinCall,
  canPayAppointment,
  type AppointmentBucket,
} from "@/lib/appointmentLifecycle";
import type { Appointment } from "@/types/domain";

const tabs: { label: string; bucket: AppointmentBucket }[] = [
  { label: "Upcoming", bucket: "UPCOMING" },
  { label: "Past", bucket: "PAST" },
  { label: "Cancelled", bucket: "CANCELLED" },
];

export default function MyAppointmentsPage() {
  const [tab, setTab] = useState(0);
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showPaid, setShowPaid] = useState(false);

  useEffect(() => {
    if (searchParams.get("paid") === "1") {
      setShowPaid(true);
      const next = new URLSearchParams(searchParams);
      next.delete("paid");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const { data: raw, isLoading } = useQuery({
    queryKey: PATIENT_APPTS_QUERY_KEY,
    queryFn: listMyAppointmentsPatient,
  });
  const data = useMemo(() => normalizeAppointmentList(raw), [raw]);

  const cancelMut = useMutation({
    mutationFn: (id: string) => cancelAppointment(id),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: PATIENT_APPTS_QUERY_KEY }),
  });

  const filtered = data.filter(
    (a) => appointmentBucket(a) === tabs[tab].bucket,
  );

  return (
    <Box>
      <PageHeader
        title="My appointments"
        subtitle={`Book, manage and cancel your consultations. Times below use your browser zone (${browserTimeZone()}).`}
        actions={
          <Button component={RouterLink} to="/doctors" variant="contained">
            Book new
          </Button>
        }
      />

      <Snackbar
        open={showPaid}
        autoHideDuration={5000}
        onClose={() => setShowPaid(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity="success" variant="filled" onClose={() => setShowPaid(false)}>
          Payment successful — your appointment is confirmed!
        </Alert>
      </Snackbar>

      <Paper variant="outlined" sx={{ mb: 2 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          sx={{ px: 1 }}
        >
          {tabs.map((t) => (
            <Tab key={t.label} label={t.label} sx={{ fontWeight: 700 }} />
          ))}
        </Tabs>
      </Paper>

      {isLoading && (
        <Box sx={{ display: "grid", placeItems: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {!isLoading && filtered.length === 0 && (
        <Paper variant="outlined" sx={{ p: 4, textAlign: "center" }}>
          <Typography color="text.secondary">
            Nothing here yet.
          </Typography>
        </Paper>
      )}

      <Stack spacing={1.5}>
        {filtered.map((a) => (
          <AppointmentRow
            key={a.id}
            a={a}
            onCancel={() => cancelMut.mutate(a.id)}
            cancelling={cancelMut.isPending && cancelMut.variables === a.id}
          />
        ))}
      </Stack>
    </Box>
  );
}

function AppointmentRow({
  a,
  onCancel,
  cancelling,
}: {
  a: Appointment;
  onCancel: () => void;
  cancelling: boolean;
}) {
  const handleCancel = () => {
    // Confirmed = already paid → cancelling triggers an auto-refund request.
    // Warn the patient so they're not surprised by the admin-approval delay.
    const message =
      a.status === "CONFIRMED"
        ? "Cancel this appointment? A refund request will be sent to our team for approval — you'll be emailed once it's processed."
        : "Cancel this appointment?";
    if (window.confirm(message)) {
      onCancel();
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2.5 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={2}
      >
        <Box>
          <Typography fontWeight={700}>
            {a.doctorName ?? "Doctor"}
            {a.doctorSpecialization && (
              <Typography component="span" color="text.secondary" fontWeight={500}>
                {" · "}
                {a.doctorSpecialization}
              </Typography>
            )}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {formatSlotStart(a.slotStart, a.timeZone)}
            {a.consultationFee != null ? ` · ₹${a.consultationFee}` : ""}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <AppointmentStatusBadge status={a.status} />
          <Button
            component={RouterLink}
            to={`/patient/appointments/${a.id}`}
            variant="outlined"
            size="small"
          >
            View
          </Button>
          {canPayAppointment(a) && (
            <Button
              component={RouterLink}
              to={`/patient/appointments/${a.id}/pay`}
              variant="contained"
              size="small"
            >
              Pay now
            </Button>
          )}
          {canCancelAppointment(a) && (
            <Button
              onClick={handleCancel}
              disabled={cancelling}
              variant="outlined"
              color="error"
              size="small"
            >
              Cancel
            </Button>
          )}
          {canJoinCall(a) && (
            <Button
              component={RouterLink}
              to={`/patient/consult/${a.id}`}
              variant="contained"
              size="small"
            >
              Join
            </Button>
          )}
        </Stack>
      </Stack>
    </Paper>
  );
}
